/**
 * The fields for whatever is selected (DESIGN §9.3).
 *
 * Every input is bound to the spec's own range, and the action select is the
 * §4.4 catalogue with a line of prose per entry — a customer picking a button's
 * job should not have to guess what `ignore_battery_optimization` means.
 *
 * The panel names what it is editing in its *header*, not here: this is the
 * body of a scroll container whose title stays put above it.
 */

import type { ModuleOut, ScreenDesignAsset } from "../api/types";
import { useI18n } from "../i18n";
import { localized } from "../i18n/localized";
import AssetPicker from "./AssetPicker";
import {
  ColorField,
  Group,
  NumberField,
  SegmentedField,
  SelectField,
  TextField,
  ToggleField,
} from "./fields";
import { moduleBlockOf } from "./modules";
import { ActionSelect, ModuleParams, ModuleProps, RowsPicker } from "./pickers";
import {
  ALIGNS,
  BUTTON_SIZES,
  DEFAULTS,
  FONTS,
  LIMITS,
  MODULE_ACTION,
  MODULE_BLOCK,
  RANGES,
  VARIANTS,
  WEIGHTS,
  type Block,
  type ButtonBlock,
  type ImageBlock,
  type ModuleBlockRef,
  type TextBlock,
} from "./spec";

export interface InspectorProps {
  block: Block;
  assets: ScreenDesignAsset[];
  /** The catalogue the release carries; `[]` until `GET /modules` answers. */
  modules: ModuleOut[];
  uploading: boolean;
  uploadError?: unknown;
  onPatch: (patch: Record<string, unknown>) => void;
  onUpload: (file: File) => void;
  onDeleteAsset: (assetId: string) => void;
}

export default function BlockInspector({
  block,
  assets,
  modules,
  uploading,
  uploadError,
  onPatch,
  onUpload,
  onDeleteAsset,
}: InspectorProps) {
  const { t, lang } = useI18n();

  /**
   * Where the block sits, first and without scrolling (DESIGN §9.3).
   *
   * "Everything is stacked in the middle" is the first thing a customer says
   * about a design, and the answer is these two controls — so they are above
   * the colours rather than at the bottom of a long form, and above the first
   * group heading rather than inside one. The stack itself is placed by the
   * Screen panel's *Vertical position* and *Gap*.
   */
  const position = (placed: TextBlock | ImageBlock | ButtonBlock) => {
    const full =
      placed.type === "text" ? false : (placed.fullWidth ?? DEFAULTS[placed.type].fullWidth);
    const align = placed.align ?? DEFAULTS[placed.type].align;
    return (
      <div className="design-position">
        {/* A full-width block has nowhere to sit, so the choice is hidden
            rather than shown doing nothing (§2 image, §2 button). */}
        {!full && (
          <SegmentedField
            label={t("design.align")}
            value={align}
            options={ALIGNS}
            labelFor={(option) => t(`design.align.${option}`)}
            onChange={(value) => onPatch({ align: value })}
          />
        )}
        {placed.type !== "text" && (
          <ToggleField
            label={t("design.fullWidth")}
            value={full}
            onChange={(fullWidth) => onPatch({ fullWidth })}
          />
        )}
      </div>
    );
  };

  return (
    <div className="design-inspector-body">
      {(block.type === "text" || block.type === "image" || block.type === "button") &&
        position(block)}

      {block.type === "text" && (
        <>
          <Group label={t("design.group.content")}>
            <TextField
              label={t("design.text")}
              value={block.text}
              max={RANGES.text.text[1]}
              multiline
              onChange={(text) => onPatch({ text })}
            />
          </Group>
          <Group label={t("design.group.style")}>
            <NumberField
              label={t("design.size")}
              unit="sp"
              value={block.size ?? DEFAULTS.text.size}
              range={RANGES.text.size}
              onChange={(size) => onPatch({ size })}
            />
            <SelectField
              label={t("design.weight")}
              value={block.weight ?? DEFAULTS.text.weight}
              options={WEIGHTS}
              labelFor={(option) => t(`design.weight.${option}`)}
              onChange={(weight) => onPatch({ weight })}
            />
            <SelectField
              label={t("design.font")}
              value={block.font ?? DEFAULTS.text.font}
              options={FONTS}
              labelFor={(option) => t(`design.font.${option}`)}
              onChange={(font) => onPatch({ font })}
            />
            <ColorField
              label={t("design.color")}
              value={block.color ?? DEFAULTS.text.color}
              onChange={(color) => onPatch({ color })}
            />
          </Group>
        </>
      )}

      {block.type === "button" && (
        <>
          <Group label={t("design.group.content")}>
            <TextField
              label={t("design.label")}
              value={block.label}
              max={RANGES.button.label[1]}
              onChange={(label) => onPatch({ label })}
            />
            <ActionSelect
              action={block.action}
              modules={modules}
              onChange={(action) => onPatch({ action })}
            />
            {block.action.type === "open_url" && (
              <TextField
                label={t("design.url")}
                value={block.action.url}
                max={LIMITS.url}
                onChange={(url) => onPatch({ action: { type: "open_url", url } })}
              />
            )}
            {/* `onPatch` merges at block level, so the whole action is
                rewritten — that is also the only way to drop a key. */}
            {block.action.type === MODULE_ACTION && (
              <ModuleParams
                action={block.action}
                modules={modules}
                onChange={(action) => onPatch({ action })}
              />
            )}
          </Group>
          <Group label={t("design.group.style")}>
            <SelectField
              label={t("design.variant")}
              value={block.variant ?? DEFAULTS.button.variant}
              options={VARIANTS}
              labelFor={(option) => t(`design.variant.${option}`)}
              onChange={(variant) => onPatch({ variant })}
            />
            <ColorField
              label={
                (block.variant ?? DEFAULTS.button.variant) === "filled"
                  ? t("design.fill")
                  : t("design.stroke")
              }
              value={block.color ?? DEFAULTS.button.color}
              onChange={(color) => onPatch({ color })}
            />
            <ColorField
              label={t("design.textColor")}
              value={block.textColor ?? DEFAULTS.button.textColor}
              onChange={(textColor) => onPatch({ textColor })}
            />
            <NumberField
              label={t("design.radius")}
              unit="dp"
              value={block.radius ?? DEFAULTS.button.radius}
              range={RANGES.button.radius}
              onChange={(radius) => onPatch({ radius })}
            />
            <SelectField
              label={t("design.buttonSize")}
              value={block.size ?? DEFAULTS.button.size}
              options={BUTTON_SIZES}
              labelFor={(option) => t(`design.buttonSize.${option}`)}
              onChange={(size) => onPatch({ size })}
            />
          </Group>
        </>
      )}

      {block.type === "image" && (
        <>
          <Group label={t("design.group.content")}>
            <AssetPicker
              assets={assets}
              value={block.asset || null}
              uploading={uploading}
              error={uploadError}
              onPick={(asset) => onPatch({ asset })}
              onUpload={onUpload}
              onDelete={onDeleteAsset}
            />
            <TextField
              label={t("design.alt")}
              value={block.alt ?? ""}
              max={80}
              onChange={(alt) => onPatch({ alt })}
            />
          </Group>
          <Group label={t("design.group.style")}>
            {!(block.fullWidth ?? DEFAULTS.image.fullWidth) && (
              <NumberField
                label={t("design.width")}
                unit="dp"
                value={block.width ?? DEFAULTS.image.width}
                range={RANGES.image.width}
                onChange={(width) => onPatch({ width })}
              />
            )}
            <NumberField
              label={t("design.radius")}
              unit="dp"
              value={block.radius ?? DEFAULTS.image.radius}
              range={RANGES.image.radius}
              onChange={(radius) => onPatch({ radius })}
            />
          </Group>
        </>
      )}

      {block.type === "spacer" && (
        <Group label={t("design.group.size")}>
          <ToggleField
            label={t("design.spacer.flex")}
            value={block.flex ?? DEFAULTS.spacer.flex}
            onChange={(flex) => onPatch({ flex })}
          />
          {!(block.flex ?? DEFAULTS.spacer.flex) && (
            <NumberField
              label={t("design.height")}
              unit="dp"
              value={block.height ?? DEFAULTS.spacer.height}
              range={RANGES.spacer.height}
              onChange={(height) => onPatch({ height })}
            />
          )}
          <p className="muted small design-hint">{t("design.spacer.hint")}</p>
        </Group>
      )}

      {block.type === "divider" && (
        <Group label={t("design.group.style")}>
          <ColorField
            label={t("design.color")}
            value={block.color ?? DEFAULTS.divider.color}
            onChange={(color) => onPatch({ color })}
          />
          <NumberField
            label={t("design.thickness")}
            unit="dp"
            value={block.thickness ?? DEFAULTS.divider.thickness}
            range={RANGES.divider.thickness}
            onChange={(thickness) => onPatch({ thickness })}
          />
          <NumberField
            label={t("design.inset")}
            unit="dp"
            value={block.inset ?? DEFAULTS.divider.inset}
            range={RANGES.divider.inset}
            onChange={(inset) => onPatch({ inset })}
          />
        </Group>
      )}

      {block.type === MODULE_BLOCK && (
        <ModuleBlockFields block={block} modules={modules} lang={lang} onPatch={onPatch} />
      )}

      {block.type === "status" && (
        <>
          <Group label={t("design.group.content")}>
            <p className="muted small design-hint">{t("design.status.hint")}</p>
            <RowsPicker
              rows={block.rows ?? [...DEFAULTS.status.rows]}
              modules={modules}
              onChange={(rows) => onPatch({ rows })}
            />
          </Group>
          <Group label={t("design.group.style")}>
            <ColorField
              label={t("design.color")}
              value={block.color ?? DEFAULTS.status.color}
              onChange={(color) => onPatch({ color })}
            />
            <ColorField
              label={t("design.mutedColor")}
              value={block.mutedColor ?? DEFAULTS.status.mutedColor}
              onChange={(mutedColor) => onPatch({ mutedColor })}
            />
            <NumberField
              label={t("design.size")}
              unit="sp"
              value={block.size ?? DEFAULTS.status.size}
              range={RANGES.status.size}
              onChange={(size) => onPatch({ size })}
            />
          </Group>
        </>
      )}
    </div>
  );
}

/**
 * A view a module draws inside the screen (MODULES §7.4, §8.3).
 *
 * Everything a customer reads here comes out of the manifest — the line of
 * prose, the fields, what they are called — because nothing in `web/` knows
 * what any particular block is (M6). What is *ours* is the one sentence a
 * `flex` block needs: it is the only block that changes where the rest of the
 * stack ends up, and a customer who drops one should not have to discover
 * that from the preview.
 */
function ModuleBlockFields({
  block,
  modules,
  lang,
  onPatch,
}: {
  block: ModuleBlockRef;
  modules: ModuleOut[];
  lang: string;
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  const { t } = useI18n();
  const declared = moduleBlockOf(modules, block);
  const description = localized(declared?.description, lang);

  return (
    <Group label={t("design.group.content")}>
      {/* A design saved against a module that is now off — or against a block
          this release no longer describes — keeps what it says, and says so
          (MODULES §8.6). Not while the catalogue is still in flight: `[]` is
          "we do not know yet", not "there is nothing". */}
      {modules.length > 0 && !declared ? (
        <p className="muted small design-hint">
          {t("design.moduleBlockUnknown", { module: block.module })}
        </p>
      ) : (
        description && <p className="muted small design-hint">{description}</p>
      )}
      {declared?.height === "flex" && (
        <p className="muted small design-hint">{t("design.moduleBlockFlex")}</p>
      )}
      {/* `onPatch` merges at block level, so `props` is rewritten whole —
          which is also the only way a field a customer emptied can leave. */}
      <ModuleProps
        block={block}
        modules={modules}
        onChange={(next) => onPatch({ props: next.props })}
      />
    </Group>
  );
}
